import { useEffect, useCallback } from 'react';
import { useNavigate, useParams, generatePath } from 'react-router-dom';
import { ValidatorsProps } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { changePanelState } from '../../store/slices/groups';
import useChartForm from '../../hooks/use-form';
import {
  useUpdateGroupMutation,
  useLazyGetAccountsGroupQuery,
} from '../../store/services/groups';

import ROUTES from '../../../../routes';

const useEdit = () => {
  const {
    accounts: { typeOptions },
    categories: { categories },
    groups: {
      groups,
      panelIsOpen,
      groupsValidation,
      selectedGroup,
      selectedGroupIsLoading,
    },
  } = useAppSelector((state) => state.charts);
  const { activePermissions } = useAppSelector((state) => state.root);

  const dispatch = useAppDispatch();

  const { categoryId, primaryAccountId, groupId } = useParams();

  const { group, control, reset, errors, handleSubmit } = useChartForm({
    groups,
    categories,
    panelIsOpen,
    schema: groupsValidation as ValidatorsProps,
  });

  const navigate = useNavigate();
  const [update, { isLoading: requestInProgress, isSuccess: onChangeSuccess }] =
    useUpdateGroupMutation();
  const [getAccountsGroup] = useLazyGetAccountsGroupQuery();

  const onSubmit = handleSubmit((values) => {
    update({
      body: {
        description: values.description as string,
        categoryId: categoryId as string,
        active: values.active,
      },
      id: groupId as string,
    });
  });

  const handleClose = useCallback(() => {
    dispatch(changePanelState(false));

    setTimeout(() => {
      navigate(
        generatePath(ROUTES.PRIMARY_ACCOUNTS, {
          categoryId: categoryId as string,
          groupId: groupId as string,
          primaryAccountId: primaryAccountId as string,
        }),
      );
    }, 100);
  }, [dispatch]);

  useEffect(() => {
    if (selectedGroup) {
      reset({
        description: selectedGroup.description,
        active: selectedGroup.active,
        categoryId,
      });
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (groupId) {
      getAccountsGroup(groupId);
    }
  }, [groupId]);

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(changePanelState(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (onChangeSuccess) {
      handleClose();
    }
  }, [onChangeSuccess, handleClose]);

  return {
    typeOptions,
    selectedGroupIsLoading,
    activePermissions,
    group,
    control,
    errors,
    requestInProgress,
    onSubmit,
    panelIsOpen,
    handleClose,
    categories,
    selectedGroup,
    categoryId,
    groupId,
  };
};

export default useEdit;
